import AddIcon from "@mui/icons-material/Add"
import { Box, CircularProgress, Typography } from "@mui/material"
import React, { useCallback, useContext, useState } from "react"
import { customPalette } from "../../../../../../../../../../theme"
import { BaseButton } from "../../../../../../../../../components/buttons/BaseButton"
import { theme } from "../../../../../../../../../components/layout/BSLayout"
import { EmptyPlaceHolder } from "../../../../components/EmptyPlaceHolder"
import { BSLevelContext } from "../../BSZoneAndLevelPage/context/BSLevelContext"
import { BSPatioContext } from "../context/BSPatioContext"
import { BSPatioInfosCard } from "./BSPatioInfosCard"
import { BSPatioFormContext, BSPatioFormContextProvider } from "../context/BSPatioFormContext"
import { BSPatioCreationFormDialog } from "./BSPatioCreationFormDialog"
import { SquareBadge } from "../../../../../../../../../components/badges/SquareBadge"
import { BSPatio } from "../../../../../../../../../core/dto/beem-shot/BSPatio/BSPatio"

export function BSPatioContainer(): React.JSX.Element {
  return (
    <BSPatioFormContextProvider>
      <BSPatioContainerCore />
    </BSPatioFormContextProvider>
  )
}

export function BSPatioContainerCore(): React.JSX.Element {
  const { bsPatioList, isLoading } = useContext(BSPatioContext)
  const { hasToControlPatio } = useContext(BSLevelContext)
  const { isDisplayNewBSPatioForm, setCurrentEditedIndex } = useContext(BSPatioFormContext)
  const [isCreationDialogOpen, setIsCreationDialogOpen] = useState<boolean>(false)

  const handleAddBSPatio: () => void = useCallback(() => {
    setCurrentEditedIndex(undefined)
    setIsCreationDialogOpen(true)
  }, [setCurrentEditedIndex])

  return (
    <Box
      border={1}
      borderRadius={3}
      p={3}
      borderColor={customPalette.paperBackground}
      boxShadow="0px 1px 3px rgba(0, 0, 0, 0.08)"
      width="100%"
      sx={{ backgroundColor: customPalette.white }}>
      <Box sx={{ py: 1, mb: 1 }}>
        {hasToControlPatio && bsPatioList && bsPatioList?.length > 0 ? (
          <Typography sx={{ color: theme.palette.error.main, fontWeight: 500 }}>
            Suite à la modification de vos niveaux, veuillez mettre à jour vos informations patios.
          </Typography>
        ) : (
          <Typography>
            Ajoutez le nombre de patios disponibles dans le bâtiment puis renseignez leurs informations.
          </Typography>
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
          <CircularProgress />
        </Box>
      ) : (
        <PatioList bsPatioList={bsPatioList} isDisplayNewBSPatioForm={isDisplayNewBSPatioForm} />
      )}

      <Box display="flex" justifyContent="end" p={1}>
        <BaseButton label="Ajouter un patio" onClick={handleAddBSPatio} icon={AddIcon} />
      </Box>

      <BSPatioCreationFormDialog isOpen={isCreationDialogOpen} setIsOpen={setIsCreationDialogOpen} />
    </Box>
  )
}

interface IPatioListProps {
  bsPatioList: BSPatio[]
  isDisplayNewBSPatioForm: boolean
}

function PatioList({ bsPatioList, isDisplayNewBSPatioForm }: IPatioListProps): React.JSX.Element {
  return bsPatioList?.length === 0 && !isDisplayNewBSPatioForm ? (
    <EmptyPlaceHolder title="Aucun patio pour le moment" />
  ) : (
    <>
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <Typography variant="body2" fontWeight="600">
          Patios ajoutés au bâtiment
        </Typography>
        <SquareBadge
          content={bsPatioList.length}
          color={customPalette.lightBlue5}
          backgroundColor={customPalette.lightBlue1}
        />
        <Box
          sx={{
            flexGrow: 1,
            height: "2px",
            backgroundColor: customPalette.paperBorder,
            marginLeft: "6px",
          }}
        />
      </Box>

      {bsPatioList?.map((patio, index) => (
        <BSPatioInfosCard
          bsPatio={patio}
          index={index}
          key={patio.id ?? `patio-${index}`}
          displayNewBSPatioForm={isDisplayNewBSPatioForm}
        />
      ))}
    </>
  )
}
